import { useCallback, useEffect, useSyncExternalStore } from "react";

import { getAuthToken, onAuthCleared } from "@/modules/auth/auth.service";
import type { HubClaim } from "./permissoes.constants";
import { listMinhasPermissoes } from "./permissoes.gateway";

export type PermissoesStatus = "idle" | "loading" | "success" | "error";

interface PermissoesSnapshot {
  status: PermissoesStatus;
  codes: ReadonlySet<string>;
}

const EMPTY: ReadonlySet<string> = new Set();
const IDLE: PermissoesSnapshot = { status: "idle", codes: EMPTY };

/**
 * Cache por sessão, compartilhado entre todos os componentes: uma única requisição por
 * sessão. É descartado no logout/401 (`onAuthCleared`) e quando o token muda.
 */
let snapshot: PermissoesSnapshot = IDLE;
let inFlight: Promise<void> | null = null;
let sessionToken: string | null = null;
let generation = 0;
const listeners = new Set<() => void>();

function publish(next: PermissoesSnapshot) {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function resetPermissoes(): void {
  generation += 1;
  inFlight = null;
  sessionToken = null;
  publish(IDLE);
}

onAuthCleared(resetPermissoes);

function load(): void {
  // Sem sessão não há o que consultar (evita laço após logout/401 com o componente montado).
  if (inFlight || !getAuthToken()) return;

  const current = generation;
  sessionToken = getAuthToken();
  publish({ status: "loading", codes: EMPTY });

  inFlight = listMinhasPermissoes()
    .then((codes) => {
      if (current === generation) publish({ status: "success", codes: new Set(codes) });
    })
    .catch(() => {
      // Fail-closed: erro mantém o conjunto vazio; o core segue barrando com 403.
      if (current === generation) publish({ status: "error", codes: EMPTY });
    });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): PermissoesSnapshot {
  return snapshot;
}

export function usePermissoes() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    // Troca de usuário sem logout explícito: o token novo invalida o cache anterior.
    if (snapshot.status !== "idle" && sessionToken !== getAuthToken()) resetPermissoes();
    if (snapshot.status === "idle") load();
  }, [state.status]);

  const hasPermissao = useCallback(
    (code: HubClaim) => state.status === "success" && state.codes.has(code),
    [state],
  );

  return { status: state.status, hasPermissao };
}
